import { useState } from 'react';
import { useI18n } from '@platform/i18n';
import type { MfaClient, MfaPendingInfo } from '@platform/api-client';
import { MfaLoginChallenge, MfaSecurityBody } from '../connected/mfa-security';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '../components/dialog';
import { toast } from '../components/sonner';

/** Reuse browser factor UI without replaying a protected write. After proof,
 * the user explicitly repeats the original action. No policy is changed. */
export function useFreshFactorVerification(client: MfaClient | undefined) {
  const { t } = useI18n();
  const [pending, setPending] = useState<MfaPendingInfo>();
  const [enroll, setEnroll] = useState(false);
  const handle = async (error: unknown): Promise<boolean> => {
    if (!client || typeof error !== 'object' || !error || !('code' in error)) return false;
    if (error.code === 'mfa_enrollment_required') { setEnroll(true); return true; }
    if (error.code === 'step_up_required') { setPending(await client.stepUp()); return true; }
    return false;
  };
  const done = () => { setPending(undefined); setEnroll(false); toast.success(t('mfa.repeatAction')); };
  const body = enroll && client
    ? <MfaSecurityBody client={client} enrollmentOnly onChanged={done} />
    : pending ? <MfaLoginChallenge client={client} pending={pending} onDone={done} onCancel={() => setPending(undefined)} /> : undefined;
  const challenge = body ? <Dialog open onOpenChange={open => { if (!open) { setPending(undefined); setEnroll(false); } }}><DialogContent framed bodySize="compact"><DialogHeader><DialogTitle>{t('mfa.security')}</DialogTitle></DialogHeader><DialogBody>{body}</DialogBody></DialogContent></Dialog> : undefined;
  return { handle, challenge };
}
